import { supabase } from '@/lib/supabase'
import { getCommissionMaturityAnchor, getCommissionStage } from '../utils/maturity'

export type AffiliateCommissionBalance = {
  pending_cents: number
  available_cents: number
  reserved_cents: number
  paid_cents: number
  accrued_cents: number
}

export type AffiliateMonthlySummaryRow = {
  year: number
  month: number
  pending_cents: number
  available_cents: number
  reserved_cents: number
  paid_cents: number
  available_commission_ids: string[]
  pending_accounts: number
  matured_accounts: number
   payout_ids: string[]
}

export async function getAffiliateCommissionBalance(affiliateUserId: string): Promise<AffiliateCommissionBalance> {
  let from = 0
  const chunk = 1000
  let pending = 0
  let available = 0
  let reserved = 0
  let paid = 0

  const nowMs = Date.now()

  for (;;) {
    const { data, error } = await supabase
      .from('v_affiliate_commissions_detailed')
      .select('amount_cents, status, paid_at, payout_id, matured_at, matures_at')
      .eq('affiliate_user_id', affiliateUserId)
      .order('period_end', { ascending: false })
      .range(from, from + chunk - 1)
    if (error) throw error
    const rows = (data as any[]) || []
    for (const r of rows) {
      const amount = Number(r?.amount_cents || 0)
      const stage = getCommissionStage(r, nowMs)
      const hasPayout = !!r?.payout_id

      if (stage === 'paid') {
        paid += amount
        continue
      }
      if (stage === 'pending') {
        pending += amount
      } else if (stage === 'matured') {
        if (hasPayout) reserved += amount
        else available += amount
      }
    }
    if (rows.length < chunk) break
    from += chunk
  }

  return {
    pending_cents: pending,
    available_cents: available,
    reserved_cents: reserved,
    paid_cents: paid,
    accrued_cents: pending + available,
  }
}

export async function getAffiliateMonthlySummary(
  affiliateUserId: string,
  months = 12
): Promise<AffiliateMonthlySummaryRow[]> {
  const now = new Date()
  const nowMs = now.getTime()
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (months - 1), 1, 0, 0, 0))

  const { data, error } = await supabase
    .from('v_affiliate_commissions_detailed')
    .select('commission_id, account_id, amount_cents, status, paid_at, payout_id, period_end, matured_at, matures_at')
    .eq('affiliate_user_id', affiliateUserId)
    .order('period_end', { ascending: false })
  if (error) throw error
  const rows = (data as any[]) || []

  const buckets = new Map<string, AffiliateMonthlySummaryRow>()
  const bucketAccounts = new Map<string, { pending: Set<string>; matured: Set<string> }>()

  for (const r of rows) {
    const anchor = getCommissionMaturityAnchor(r)
    if (!anchor || anchor < start) continue
    const year = anchor.getUTCFullYear()
    const month = anchor.getUTCMonth() + 1
    const key = `${year}-${month.toString().padStart(2, '0')}`
    let bucket = buckets.get(key)
    if (!bucket) {
      bucket = {
        year,
        month,
        pending_cents: 0,
        available_cents: 0,
        reserved_cents: 0,
        paid_cents: 0,
        available_commission_ids: [],
        pending_accounts: 0,
        matured_accounts: 0,
        payout_ids: [],
      }
      buckets.set(key, bucket)
    }
    let accBucket = bucketAccounts.get(key)
    if (!accBucket) {
      accBucket = { pending: new Set<string>(), matured: new Set<string>() }
      bucketAccounts.set(key, accBucket)
    }

    const accountId = r?.account_id ? String(r.account_id) : null
    const payoutId = r?.payout_id ? String(r.payout_id) : null
    const amount = Number(r?.amount_cents || 0)
    const stage = getCommissionStage(r, nowMs)
    const hasPayout = !!r?.payout_id

    if (payoutId && !bucket.payout_ids.includes(payoutId)) {
      bucket.payout_ids.push(payoutId)
    }

    if (stage === 'paid') {
      bucket.paid_cents += amount
      continue
    }
    if (stage === 'pending') {
      bucket.pending_cents += amount
      if (accountId) accBucket.pending.add(accountId)
    } else if (stage === 'matured') {
      if (hasPayout) {
        bucket.reserved_cents += amount
      } else {
        bucket.available_cents += amount
        if (r.commission_id) bucket.available_commission_ids.push(String(r.commission_id))
      }
      if (accountId) accBucket.matured.add(accountId)
    }
  }

  const list = Array.from(buckets.values())
  for (const row of list) {
    const key = `${row.year}-${row.month.toString().padStart(2, '0')}`
    const accBucket = bucketAccounts.get(key)
    row.pending_accounts = accBucket ? accBucket.pending.size : 0
    row.matured_accounts = accBucket ? accBucket.matured.size : 0
  }
  list.sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year
    return b.month - a.month
  })
  return list
}
